import { PrinterOutlined } from '@ant-design/icons';
import { Button, Form, Space, Table } from 'antd';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { pagination } from '../../../../common/utils/common.utils';
import { excelDateRangeFormat } from '../../../../common/utils/excelDateRangeFormat';
import DownloadExcelReport from '../../../../components/common/ExcelReport';
import CommonViewReport from '../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport';
import ReportsHeader from '../../../../components/common/ReportsHeader';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import FormHeaderTitle from '../../../Invoice(Visa)/Components/FormHeaderTitle';
import { useLazyGetGDSReportQuery } from '../endpoints/GdsReportEndpoints';
import { IGDSReportFromDataType, IGDSReportsDataType } from '../types/GdsReportType';
import gdsReportColumn from '../utils/gdsReportColumn';

const GdsReport = () => {
  const [paramData, setParamData] = useState<{
    date_range: string;
    id: number | string;
  }>();
  const currentDate = dayjs().format('DD-MM-YYYY');
  const [dateRange, setDateRange] = useState(`from_date=${currentDate}&to_date=${currentDate}`);
  const [form] = Form.useForm();

  const [fetchGDSReport, { data, isLoading }] = useLazyGetGDSReportQuery({
    selectFromResult: (cache) => {
      const data = cache.data?.data;
      const dataToReturn: IGDSReportsDataType[] = [];
      if (data) {
        for (let i = 0; i < data.length; i++) {
          const element = data[i];
          dataToReturn.push({
            ...element,
            key: i + 1,
          });
        }
      }
      return {
        ...cache,
        data: dataToReturn,
      };
    },
  });
  const onFinish = async (values: IGDSReportFromDataType) => {
    const setFrom_date = dayjs(values.date_range[0]).format('YYYY-MM-DD');
    const setTo_date = dayjs(values.date_range[1]).format('YYYY-MM-DD');
    const date_range = `from_date=${setFrom_date}&to_date=${setTo_date}`;
    setDateRange(date_range);
    const body = {
      date_range: date_range,
      gds: values.gds,
    };
    await fetchGDSReport(body);
    setParamData({
      date_range: date_range,
      id: '',
    });
  };

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `GDS_Report`,
  });

  // @HANDLE CONTENTS
  const jsx_com = (
    <Table
      size='small'
      bordered
      pagination={pagination(data.length)}
      columns={gdsReportColumn(paramData)}
      dataSource={data}
      loading={{ spinning: isLoading, indicator: loadingIndicator }}
    />
  );

  // @HANDLE PRINT WITH HEADER FOOTER
  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx_com}
        printRef={componentRef}
        title={{ info_title: '', title: 'GDS Report' }}
        extraInfo={{
          date:
            paramData?.date_range
              ?.split('&')
              ?.map((param) => dayjs(param.split('=')[1])?.format('DD-MM-YYYY'))
              ?.join(' to ') || '',
        }}
      />
    </div>
  );
  return (
    <>
      <BreadCrumb arrOfOption={['Reports', 'GDS Report']} />
      <Space style={{ marginBottom: '1rem' }}>
        <Button type='primary' onClick={handlePrint}>
          <PrinterOutlined />
          Print
        </Button>

        <DownloadExcelReport
          excelName={`gds_report_${excelDateRangeFormat(dateRange as string)}`}
          description={`GDS REPORT`}
          excelTableHead={['GDS Name', 'Ticket No', 'Route', 'PNR', 'Fly Date', 'Total Segment']}
          excelData={
            data?.length
              ? data.map((item: any) => ({
                  'GDS Name': item?.gds_name || 'N/A',
                  'Ticket No': item?.airticket_ticket_no || 'N/A',
                  Route: item?.airline_iata_code || 'N/A',
                  PNR: item?.airticket_pnr || 'N/A',
                  'Fly Date': item?.airticket_journey_date
                    ? dayjs(item.airticket_journey_date).format('DD MM YYYY')
                    : '',
                  'Total Segment': item?.airticket_segment || '0',
                }))
              : []
          }
        />
      </Space>
      <Form
        layout='vertical'
        form={form}
        onFinish={onFinish}
        initialValues={{ date_range: [dayjs(), dayjs()], gds: 'all' }}
      >
        <ReportsHeader date_range required reports_select showGds showAll />
      </Form>

      <FormHeaderTitle title='GDS Report' />

      {/* //----------------//------------// @LEDGER TABLE HERE //----------------//------------// */}
      {jsx_com}

      {/*-----//---------//--------- @PRINTABLE CONTENT -----------//-----------//-----*/}
      {print_content}
    </>
  );
};

export default GdsReport;
